import React, { useState, useEffect } from "react";
import { processService } from "../../services/process.service";
import { providerService } from "../../services/provider.service";
import { clicksignService } from "../../services/clicksign.service";
import { useProcess } from "../../contexts/ProcessContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Plus, Trash2, User, Pencil, FileText } from "lucide-react";

const { addProcessoSigner, removeProcessoSigner, updateProcessoSigner, updateDocumentoSigners } = processService;

const QUALIFICACAO_OPTIONS = [
  { value: "party",       label: "Parte" },
  { value: "consenting",  label: "Anuente" },
  { value: "transferor",  label: "Cedente / Outorgante" },
  { value: "transferee",  label: "Cessionário / Outorgado" },
  { value: "witness",     label: "Testemunha" },
  { value: "attorney",    label: "Procurador" },
  { value: "guarantor",   label: "Fiador" },
  { value: "employer",    label: "Empregador" },
  { value: "employee",    label: "Empregado" },
  { value: "contractor",  label: "Contratante" },
  { value: "contractee",  label: "Contratado" },
];

const AUTENTICACOES_OPTIONS = [
  { value: "email",              label: "E-mail" },
  { value: "sms",                label: "SMS" },
  { value: "whatsapp",           label: "WhatsApp" },
  { value: "handwritten",        label: "Assinatura Manuscrita" },
  { value: "selfie",             label: "Selfie" },
  { value: "liveness",           label: "Prova de Vida" },
  { value: "facial_biometrics",  label: "Biometria Facial" },
  { value: "biometric",          label: "Biométrico" },
  { value: "official_document",  label: "Documento Oficial" },
  { value: "address_proof",      label: "Comprovante de Endereço" },
  { value: "pix",                label: "Pix" },
  { value: "icp_brasil",         label: "ICP-Brasil" },
];

const EMPTY_SIGNER = {
  name: "",
  email: "",
  cpf: "",
  role: "",
  phone_number: "",
  qualificacao: "party",
  autenticacoes: ["email"],
};

function qualificacaoLabel(value) {
  return QUALIFICACAO_OPTIONS.find(o => o.value === value)?.label ?? value;
}

function autenticacoesLabel(values) {
  if (!Array.isArray(values) || values.length === 0) return "—";
  return values.map(v => AUTENTICACOES_OPTIONS.find(o => o.value === v)?.label ?? v).join(", ");
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function SignersTab() {
  const { processo, refresh: onRefresh } = useProcess();
  const [isSignerDialogOpen, setIsSignerDialogOpen] = useState(false);
  const [editingSignerId, setEditingSignerId] = useState(null);
  const [newSignerData, setNewSignerData] = useState(EMPTY_SIGNER);
  const [autenticacoesDisponiveis, setAutenticacoesDisponiveis] = useState(AUTENTICACOES_OPTIONS);
  const [docSignerState, setDocSignerState] = useState({});
  const [processoTemplates, setProcessoTemplates] = useState([]);

  const templateDocs = (processo.documentos || []).filter(d => !d.deleted_at);
  const hasSignerSection = processo.signers?.length > 0 && templateDocs.length > 0;

  useEffect(() => {
    const docs = (processo.documentos || []).filter(d => !d.deleted_at);
    if (docs.length > 0) {
      const initial = {};
      docs.forEach(doc => {
        initial[doc.id] = (doc.signers || []).map(s => s.id);
      });
      setDocSignerState(initial);
    }
  }, [processo.documentos]);

  useEffect(() => {
    if (processo?.id) {
      clicksignService.getProcessoTemplates(processo.id)
        .then(setProcessoTemplates)
        .catch(() => {});
    }
  }, [processo?.id]);

  const docLabel = (doc) => {
    if (doc.clicksign_template_id) {
      const t = processoTemplates.find(t => t.id === doc.clicksign_template_id);
      if (t?.nome) return t.nome;
    }
    return capitalize(doc.tipo);
  };

  useEffect(() => {
    providerService.getProviders().then(providers => {
      const active = providers.find(p => p.is_active);
      if (active?.name === "clicksign") {
        const habilitadas = active.config?.autenticacoes_habilitadas;
        if (Array.isArray(habilitadas) && habilitadas.length > 0) {
          setAutenticacoesDisponiveis(AUTENTICACOES_OPTIONS.filter(o => habilitadas.includes(o.value)));
        }
      }
    }).catch((err) => {
      toast.error(err?.response?.data?.detail || "Erro ao carregar configuração do provider.");
    });
  }, []);

  const handleSaveSigner = async () => {
    if (!processo) return;
    try {
      if (editingSignerId) {
        await updateProcessoSigner(processo.id, editingSignerId, newSignerData);
        toast.success("Assinante atualizado");
      } else {
        await addProcessoSigner(processo.id, newSignerData);
        toast.success("Assinante adicionado");
      }
      setIsSignerDialogOpen(false);
      setEditingSignerId(null);
      setNewSignerData(EMPTY_SIGNER);
      onRefresh();
    } catch (e) {
      toast.error("Erro ao salvar assinante: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleRemoveSigner = async (signerId) => {
    if (!processo) return;
    try {
      await removeProcessoSigner(processo.id, signerId);
      toast.success("Assinante removido");
      onRefresh();
    } catch (e) {
      toast.error("Erro ao remover assinante");
    }
  };

  const toggleAutenticacao = (value) => {
    setNewSignerData(prev => {
      const current = prev.autenticacoes || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, autenticacoes: updated };
    });
  };

  const openEditDialog = (signer) => {
    setEditingSignerId(signer.id);
    setNewSignerData({
      name: signer.name,
      email: signer.email,
      cpf: signer.cpf || "",
      phone_number: signer.phone_number || "",
      role: signer.role || "",
      qualificacao: signer.qualificacao,
      autenticacoes: signer.autenticacoes || ["email"],
    });
    setIsSignerDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingSignerId(null);
    setNewSignerData(EMPTY_SIGNER);
    setIsSignerDialogOpen(true);
  };

  const handleToggleDocSigner = async (doc, signerId) => {
    const current = docSignerState[doc.id] || [];
    const next = current.includes(signerId)
      ? current.filter(id => id !== signerId)
      : [...current, signerId];

    setDocSignerState(prev => ({ ...prev, [doc.id]: next }));

    try {
      await updateDocumentoSigners(processo.id, doc.id, next);
    } catch (e) {
      setDocSignerState(prev => ({ ...prev, [doc.id]: current }));
      toast.error("Erro ao atualizar assinantes do documento");
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <User className="h-5 w-5" /> Assinantes (Advogados / Testemunhas)
          </h3>
          <Dialog open={isSignerDialogOpen} onOpenChange={(open) => {
            setIsSignerDialogOpen(open);
            if (!open) {
              setEditingSignerId(null);
              setNewSignerData(EMPTY_SIGNER);
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openAddDialog}><Plus className="mr-2 h-4 w-4" /> Adicionar Assinante</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingSignerId ? "Editar Assinante" : "Adicionar Assinante"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={newSignerData.name}
                    onChange={e => setNewSignerData({ ...newSignerData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newSignerData.email}
                    onChange={e => setNewSignerData({ ...newSignerData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>CPF</Label>
                  <Input
                    value={newSignerData.cpf ?? ""}
                    onChange={e => setNewSignerData({ ...newSignerData, cpf: e.target.value })}
                    placeholder="000.000.000-00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={newSignerData.phone_number}
                    onChange={e => setNewSignerData({ ...newSignerData, phone_number: e.target.value })}
                    placeholder="Apenas números (necessário para WhatsApp)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Papel descritivo (opcional)</Label>
                  <Input
                    value={newSignerData.role}
                    onChange={e => setNewSignerData({ ...newSignerData, role: e.target.value })}
                    placeholder="ex: Advogado da empresa"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Qualificação ClickSign</Label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    value={newSignerData.qualificacao}
                    onChange={e => setNewSignerData({ ...newSignerData, qualificacao: e.target.value })}
                  >
                    {QUALIFICACAO_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Define o papel jurídico do assinante no documento ClickSign.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Métodos de Autenticação</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {autenticacoesDisponiveis.map(opt => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={(newSignerData.autenticacoes || []).includes(opt.value)}
                          onChange={() => toggleAutenticacao(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Selecione ao menos um método de autenticação.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleSaveSigner} disabled={!newSignerData.name || !newSignerData.email}>
                  Salvar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Qualificação</TableHead>
              <TableHead>Autenticações</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processo.signers?.map(signer => (
              <TableRow key={signer.id}>
                <TableCell>{signer.name}</TableCell>
                <TableCell>{signer.email}</TableCell>
                <TableCell>{signer.cpf || "—"}</TableCell>
                <TableCell>{qualificacaoLabel(signer.qualificacao)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {autenticacoesLabel(signer.autenticacoes)}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="sm" title="Editar" onClick={() => openEditDialog(signer)}>
                    <Pencil className="h-4 w-4 text-blue-500" />
                  </Button>
                  <Button variant="ghost" size="sm" title="Remover" onClick={() => handleRemoveSigner(signer.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {processo.signers?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Nenhum assinante cadastrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {hasSignerSection && (
        <Card className="p-6">
          <h3 className="text-xl font-semibold flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5" /> Documentos por Assinante
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Documento</TableHead>
                  {processo.signers.map(signer => (
                    <TableHead key={signer.id} className="text-center whitespace-nowrap">
                      <div className="font-medium">{signer.name}</div>
                      <div className="text-xs font-normal text-muted-foreground">
                        {qualificacaoLabel(signer.qualificacao)}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {templateDocs.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">{docLabel(doc)}</TableCell>
                    {processo.signers.map(signer => {
                      const checked = (docSignerState[doc.id] || []).includes(signer.id);
                      return (
                        <TableCell key={signer.id} className="text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer rounded"
                            checked={checked}
                            onChange={() => handleToggleDocSigner(doc, signer.id)}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
